import {Icon} from './Icon'
import {type ReactNode, type TouchEvent, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

export interface ModalProps {
  /** Controls whether the modal is visible */
  isOpen: boolean
  /** Callback when modal should close */
  onClose: () => void
  /** Modal content */
  children: ReactNode
  /** Optional title displayed in the header */
  title?: string
  /** Optional modal width for desktop (defaults to 400px) */
  width?: 'sm' | 'md' | 'lg' | 'xl'
  /** Position on screen - 'side' for right panel, 'center' for centered modal */
  position?: 'side' | 'center'
  /** Show border on header (defaults to true) */
  showHeaderBorder?: boolean
  /** Show header with close button (defaults to true) */
  showHeader?: boolean
  /** Optional callback for back navigation - renders a back arrow in the top left */
  onBack?: () => void
  /** Mobile height for side panel mode */
  mobileSideHeight?: 'full' | '80' | 'offset-80'
  /** Allow swipe-down dismiss on mobile side panel */
  enableSwipeDownToClose?: boolean
}

const widthClasses = {
  sm: 'md:w-[320px] 3xl:w-[400px]',
  md: 'md:w-[384px] lg:w-[410px] xl:w-[580px] 3xl:w-[770px]',
  lg: 'md:w-[600px] 3xl:w-[770px]',
  xl: 'md:w-[800px] 3xl:w-[900px]',
}

const sidePanelWidthClasses = {
  sm: 'lg:w-[320px] 3xl:w-[400px]',
  md: 'lg:w-[410px] xl:w-[580px] 3xl:w-[770px]',
  lg: 'lg:w-[600px] 3xl:w-[770px]',
  xl: 'lg:w-[800px] 3xl:w-[900px]',
}

/**
 * Modal Component
 *
 * A flexible modal dialog with support for side panels and centered modals.
 *
 * **Responsive behavior:**
 * - Mobile/tablet: Full screen with slide up animation
 * - Desktop (lg+): Side panel or centered modal based on position prop
 *
 * **Features:**
 * - Portal rendering (outside root DOM)
 * - Focus trap
 * - ESC key to close
 * - Backdrop click to close
 * - Body scroll lock
 * - Smooth slide animations
 *
 * **Usage:**
 * ```tsx
 * <Modal isOpen={isOpen} onClose={handleClose} title="Settings">
 *   <div>Modal content</div>
 * </Modal>
 * ```
 *
 * **Figma:** (Add Figma link if available)
 */
export const Modal = ({
  isOpen,
  onClose,
  children,
  title,
  width = 'md',
  position = 'side',
  showHeaderBorder = true,
  showHeader = true,
  onBack,
  mobileSideHeight = 'full',
  enableSwipeDownToClose = false,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const touchStartYRef = useRef<number | null>(null)

  // Handle ESC key to close
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Handle animation state
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true)
    }
  }, [isOpen])

  // Focus trap - focus first focusable element
  useEffect(() => {
    if (isOpen && isAnimating && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (focusableElements.length > 0) {
        focusableElements[0]?.focus()
      }
    }
  }, [isOpen, isAnimating])

  if (!isOpen && !isAnimating) return null

  const isSidePanel = position === 'side'
  const isMobileViewport = typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  const canSwipeDownClose = enableSwipeDownToClose && isSidePanel && isMobileViewport

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (!canSwipeDownClose) return
    const targetElement = event.target as HTMLElement | null
    const startsFromHandle = targetElement?.closest('[data-modal-drag-handle]')
    if (!startsFromHandle) return
    touchStartYRef.current = event.touches[0]?.clientY ?? null
    setIsDragging(true)
  }

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (!canSwipeDownClose || touchStartYRef.current === null) return
    const currentY = event.touches[0]?.clientY ?? touchStartYRef.current
    const nextDragY = Math.max(0, currentY - touchStartYRef.current)
    setDragY(nextDragY)
  }

  const handleTouchEnd = () => {
    if (!canSwipeDownClose) return
    const shouldClose = dragY > 100
    touchStartYRef.current = null
    setIsDragging(false)
    setDragY(0)
    if (shouldClose) {
      onClose()
    }
  }

  const handleTouchCancel = () => {
    if (!canSwipeDownClose) return
    touchStartYRef.current = null
    setIsDragging(false)
    setDragY(0)
  }

  return createPortal(
    <div
      className={`inset-0 fixed z-50 flex ${
        isSidePanel ? 'lg:items-stretch lg:justify-end items-end' : 'items-center justify-center'
      }`}
      role="dialog"
      data-testid="modal"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* Backdrop with fade animation */}
      <div
        className={`inset-0 fixed bg-black-alpha-500 transition-opacity ${
          isAnimating && isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
        data-testid="modal-overlay"
      />

      {/* Modal Panel with slide animation */}
      <div
        ref={modalRef}
        className={`shadow-xl relative z-10 flex w-full flex-col bg-surface ${isSidePanel ? `${mobileSideHeight === '80' ? 'lg:h-full lg:rounded-none h-[80dvh] rounded-t-lg' : mobileSideHeight === 'offset-80' ? 'lg:h-full lg:rounded-none h-[calc(100dvh-80px)] rounded-t-lg' : 'h-full'} ${sidePanelWidthClasses[width]}` : `${widthClasses[width]} max-h-[90vh] rounded-lg`} ${isDragging ? '' : 'ease-out transition-transform'} ${
          isAnimating && isOpen
            ? isSidePanel
              ? 'translate-y-0 lg:translate-y-0 lg:translate-x-0' // Side panel: fully visible
              : 'translate-y-0 scale-100' // Centered: fully visible
            : isSidePanel
              ? 'lg:translate-y-0 lg:translate-x-full translate-y-full' // Side panel: slide down (mobile/tablet), slide right (desktop)
              : 'translate-y-full scale-95' // Centered: slide down and scale
        }`}
        onTransitionEnd={() => {
          if (!isOpen) {
            setIsAnimating(false)
          }
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        style={dragY > 0 ? {transform: `translateY(${dragY}px)`} : undefined}
        data-testid="modal-panel"
      >
        {/* Header */}
        {showHeader && (
          <div
            className={`md:mb-0 md:pt-6 md:px-6 md:pb-[14px] lg:p-10 -mb-8 flex items-center justify-between p-6 ${showHeaderBorder ? 'border-b border-border' : ''}`}
          >
            {onBack ? (
              <button
                onClick={onBack}
                className="flex h-40 w-40 items-center justify-center rounded-full transition-colors hover:bg-surface-hover"
                type="button"
                aria-label="Go back"
                data-testid="modal-button-back"
              >
                <Icon name="chevron-left" size={20} color="text" />
              </button>
            ) : (
              <div className="h-40 w-40" aria-hidden="true" />
            )}
            <button
              onClick={onClose}
              className="flex h-40 w-40 items-center justify-center rounded-full transition-colors hover:bg-surface-hover"
              type="button"
              aria-label="Close modal"
              data-testid="modal-button-close"
            >
              <Icon name="close" size={24} color="text" />
            </button>
          </div>
        )}

        {/* Title - positioned below header, does NOT scroll */}
        {title && (
          <div className="lg:px-20 -mt-2 flex shrink-0 items-center justify-center px-16 3xl:px-120">
            <h1
              id="modal-title"
              className="text-xl leading-xl font-medium -tracking-[0.015em] text-text"
              data-testid="modal-title"
            >
              {title}
            </h1>
          </div>
        )}

        {/* Content - scrollable area */}
        <div className="flex-1 overflow-hidden">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
